import styles from "./MainInfo.module.scss";
import { Button, ButtonSize, NewTextField } from "@bale/ui";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import Footer from "components/commons/footer/Footer";
import {
  LinkErrors,
  validateForbiddenPlatforms,
  validateBaleUrl,
} from "components/content/dialogAd/steps/infoStep/validateInfo";
import { OrderAdActions, OrderAdPages } from "core/orderAd/orderAd.slice";
import {
  FileErrors,
  FileValidators,
  useFileHandler,
} from "hooks/useFileHandler";
import {
  GeneralErrors,
  GeneralValidators,
  useInputHandler,
} from "hooks/useInputHandler";
import { strings } from "intl/fa";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MAX_SPONSORED_MESSAGE_IMAGE_SIZE } from "utils/constants";
import { countLinks } from "@bale/utils";
import { WebUrl } from "utils/regex";
import { AppDispatch, useAppDispatch, useAppSelector } from "utils/store";
import {
  disableButton,
  formatAdUrl,
  formatStrings,
  getFileFromBlob,
} from "utils/utils";
import { Bubble } from "../4_preview/Bubble";
import useValidateChannelMembersCount from "hooks/useValidateChannelMembersCount";
import { MIN_MEMBER } from "components/content/new_flow/info/constants";
import { toast } from "react-toastify";
import ImageCropInput from "components/commons/ImageInput/ImageCropInput";

const SponsoredMessageSteps = {
  MainInfo: strings.sponsored_messages.submit_steps.MainInfo,
  ViewOptions: strings.sponsored_messages.submit_steps.ViewOptions,
  Moreinfo: strings.sponsored_messages.submit_steps.Moreinfo,
};

export const sponsoredMessageSteps = [
  {
    step: 1,
    label: SponsoredMessageSteps.MainInfo,
    callback: (dispatch: AppDispatch) =>
      dispatch(OrderAdActions.setPage(OrderAdPages.SponsoredMessageMainInfo)),
  },
  {
    step: 2,
    label: SponsoredMessageSteps.ViewOptions,
    callback: (dispatch: AppDispatch) =>
      dispatch(
        OrderAdActions.setPage(OrderAdPages.SponsoredMessageViewOptions)
      ),
  },
  {
    step: 3,
    label: SponsoredMessageSteps.Moreinfo,
    callback: (dispatch: AppDispatch) =>
      dispatch(OrderAdActions.setPage(OrderAdPages.DialogMoreInfo)),
  },
];

interface Props {
  isCampaign?: boolean;
}
const MainInfo: React.FC<React.PropsWithChildren<Props>> = ({ isCampaign }) => {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLImageElement>(null);
  const [croppedImageURL, setCroppedImageURL] = useState<string | undefined>();
  const { link, link_title, description } = useAppSelector(
    (state) => state.orderAd.info
  );
  const validateChannelMemberCount = useValidateChannelMembersCount(MIN_MEMBER);

  const adImageHandler = useFileHandler({
    rules: [
      FileValidators.required,
      FileValidators.maximumSize(MAX_SPONSORED_MESSAGE_IMAGE_SIZE),
    ],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [FileErrors.MaximumSize]: strings.common.error.image_max_size,
      [FileErrors.MaxImageHeight]: strings.common.error.image_max_height,
    },
  });

  const submitHandler = () => {
    if (
      buttonLinkInputHandler.error ||
      buttonTitleInputHandler.error ||
      adTextInputHandler.error ||
      adImageHandler.error
    ) {
      return;
    }

    validateChannelMemberCount(
      formatAdUrl(buttonLinkInputHandler.rawValue),
      (res, notFound) => {
        if (res) {
          dispatch(
            OrderAdActions.setOrderAdInfo({
              description: adTextInputHandler.rawValue.trim(),
              link_title: isJoinLink
                ? strings.order.sponsored.info.join
                : buttonTitleInputHandler.rawValue.trim(),
              link: buttonLinkInputHandler.rawValue,
              imageWidth: containerRef.current?.getBoundingClientRect().width,
              imageHeight: containerRef.current?.getBoundingClientRect().height,
              croppedImageURL: croppedImageURL,
            })
          );
          if (isCampaign) {
            dispatch(
              OrderAdActions.setPage(
                OrderAdPages.CampaignSponsoredMessageViewOptions
              )
            );
          } else {
            dispatch(
              OrderAdActions.setPage(OrderAdPages.SponsoredMessageViewOptions)
            );
          }
        } else {
          toast.info(strings.sponsored_messages.low_members);
        }
      }
    );
  };

  const [linkError, setLinkError] = useState<string | undefined>(undefined);

  const buttonTitleInputHandler = useInputHandler({
    initialValue: link_title,
    rules: [
      GeneralValidators.NotAnyEnglish,
      GeneralValidators.required,
      GeneralValidators.maxLength(15),
      GeneralValidators.NotAllEnglish,
    ],
    errorMessages: {
      [GeneralErrors.MaximumLength]: strings.order.sponsored.info.long_title,
      [GeneralErrors.Required]: strings.common.error.required,
      [GeneralErrors.NoAnyEnglish]:
        strings.order.sponsored.info.button_english_error,
    },
  });

  const adLinkRules = [
    GeneralValidators.required,
    GeneralValidators.regexMatch(WebUrl),
    validateBaleUrl,
    validateForbiddenPlatforms,
  ];

  const buttonLinkInputHandler = useInputHandler({
    initialValue: link,
    rules: adLinkRules,
    showDigitsLatin: true,
    errorMessages: {
      [GeneralErrors.RegexMatch]:
        strings.dialog_ad_form.info_step.invalid_link_error,
      [GeneralErrors.ExcludeWithRegex]:
        strings.dialog_ad_form.info_step.join_link_error,
      [GeneralErrors.Required]: strings.common.error.required,
      [LinkErrors.ForbiddenPlatform]:
        strings.dialog_ad_form.info_step.errors.forbiddenPlatforms,
      [LinkErrors.WrongBaleUrl]:
        strings.dialog_ad_form.info_step.invalid_link_error,
    },
  });

  const isJoinLink = useMemo<boolean>(() => {
    return buttonLinkInputHandler.rawValue.includes("ble.ir/join");
  }, [buttonLinkInputHandler]);

  const adTextInputHandler = useInputHandler({
    initialValue: description,
    rules: [GeneralValidators.required, GeneralValidators.maxLength(200)],
  });

  useEffect(() => {
    const totalLinks = countLinks(adTextInputHandler.rawValue);
    if (totalLinks > 0) {
      setLinkError(strings.order.sponsored.info.errors.cant_have_link);
    } else {
      setLinkError(undefined);
    }
  }, [adTextInputHandler.rawValue]);

  const isDisable = useMemo<boolean>(() => {
    if (
      buttonLinkInputHandler.error ||
      adTextInputHandler.error ||
      linkError ||
      buttonTitleInputHandler.error
    ) {
      return true;
    }
    if (isJoinLink) {
      return (
        buttonLinkInputHandler.rawValue === "" ||
        adTextInputHandler.rawValue === ""
      );
    } else {
      return (
        buttonLinkInputHandler.rawValue === "" ||
        buttonTitleInputHandler.rawValue === "" ||
        adTextInputHandler.rawValue === ""
      );
    }
  }, [
    buttonLinkInputHandler,
    buttonTitleInputHandler,
    adTextInputHandler,
    isJoinLink,
  ]);

  return (
    <>
      <CardWrapper className={styles.Card}>
        {/* <ProgressSteps steps={sponsoredMessageSteps} currentStep={1} /> */}
        {/* <p
          className={styles.PreviewLink}
          onClick={() => setShowPreviewModal(true)}
        >
          {strings.sponsored_messages.preview.order_preview}
        </p> */}

        <NewTextField
          {...buttonLinkInputHandler}
          placeholder={strings.order.sponsored.info.button_link_placeholder}
          label={strings.order.sponsored.info.button_link}
          direction="rtl"
          helpText={strings.order.sponsored.info.button_link_helper}
        />

        <NewTextField
          {...buttonTitleInputHandler}
          value={
            isJoinLink
              ? strings.order.sponsored.info.join
              : buttonTitleInputHandler.value
          }
          placeholder={strings.order.sponsored.info.button_title_placeholer}
          disabled={isJoinLink}
          label={strings.order.sponsored.info.button_title}
          direction="rtl"
          helpText={
            isJoinLink
              ? strings.sponsored_messages.errors.join_link
              : strings.order.sponsored.info.button_title_helper
          }
          maxLength={15}
          showMaxLength={true}
          errorText={isJoinLink ? undefined : buttonTitleInputHandler.error}
        />
        <NewTextField
          multiline
          maxLength={200}
          {...adTextInputHandler}
          errorText={adTextInputHandler.error || linkError}
          direction="rtl"
          placeholder={strings.order.sponsored.info.ad_text_placeholder}
          showMaxLength={true}
          label={strings.order.sponsored.info.ad_text}
        />
        <ImageCropInput
          {...adImageHandler}
          helpText={strings.sponsored_messages.image_size_format_info}
          beforeSelectLabel={strings.common.input.optional_image.label}
          afterSelectLabel={strings.common.input.optional_image.label}
          croppedImageURL={croppedImageURL}
          setCroppedImageURL={setCroppedImageURL}
        />
        {croppedImageURL && (
          <>
            <p className={styles.ImageValidation}>
              {strings.sponsored_messages.preview.order_preview}
            </p>
            <Bubble
              caption={adTextInputHandler.rawValue}
              button_link={buttonLinkInputHandler.value}
              button_title={buttonTitleInputHandler.rawValue}
              containerRef={containerRef}
              imageSrc={croppedImageURL}
            />
          </>
        )}
      </CardWrapper>
      <Footer>
        <Button
          onClick={submitHandler}
          text={strings.dialog_ad_form.info_step.submit}
          size={ButtonSize.XLarge}
          disable={isDisable}
          disabled={isDisable}
          data-testid="confirm-button"
        />
      </Footer>
    </>
  );
};

export default MainInfo;
